import { QRCodeSVG } from "qrcode.react";
import { X, Printer, ShieldCheck, Clock } from "lucide-react";
import { formatEU } from "./public-book/formatters";

/**
 * DigitalRentalPass (v4.0.5) — "Digital Key Pass" card carrying a QR code
 * that deep-links the guest straight into `/manage-booking`. Rendered both in
 * the post-reservation confirmation screen and from the self-service portal.
 * Frontend-only: everything is derived from the booking object already loaded.
 */
export default function DigitalRentalPass({
  booking,
  companyName = "",
  onClose,
  embedded = false,
}) {
  const reference = (booking?.booking_reference || booking?.id || "").slice(0, 8).toUpperCase();
  const email = booking?.customer_email || booking?.email || booking?.user_email || "";
  const passUrl = `${window.location.origin}/manage-booking?ref=${reference}&email=${encodeURIComponent(email)}`;

  const ready = Boolean(
    booking?.driver_verification?.verified_at
    || (String(booking?.id_passport || "").trim() && String(booking?.license_number || "").trim()),
  );

  const vehicleName =
    booking?.vehicle_snapshot?.name || booking?.vehicle_name || booking?.vehicle_snapshot?.brand || "Vehicle";

  return (
    <div
      className="bg-neutral-950 text-white rounded-2xl overflow-hidden shadow-2xl w-full max-w-md mx-auto"
      data-testid="digital-rental-pass"
    >
      <div className="px-6 pt-6 pb-5 border-b border-dashed border-white/15">
        <p className="text-[10px] uppercase tracking-[0.35em] text-white/50">
          {companyName || "Car Rental App"}
        </p>
        <h3 className="font-display text-2xl mt-1.5 leading-tight">Digital Key Pass</h3>

        <div className="mt-5 space-y-3 text-left">
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-white/45">Booking reference</p>
            <p className="font-mono text-lg mt-0.5" data-testid="pass-reference">{reference}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-white/45">Vehicle</p>
            <p className="text-base mt-0.5" data-testid="pass-vehicle">{vehicleName}</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-[10px] uppercase tracking-[0.28em] text-white/45">Pickup</p>
              <p className="text-sm mt-0.5 tabular-nums">
                {formatEU(booking?.pickup_date)} {booking?.pickup_time || ""}
              </p>
              <p className="text-xs text-white/55">{booking?.pickup_location || "—"}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.28em] text-white/45">Return</p>
              <p className="text-sm mt-0.5 tabular-nums">
                {formatEU(booking?.return_date)} {booking?.return_time || ""}
              </p>
              <p className="text-xs text-white/55">{booking?.return_location || "—"}</p>
            </div>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-white/45">Driver</p>
            <p className="text-sm mt-0.5" data-testid="pass-driver">{booking?.full_name || email || "—"}</p>
          </div>
        </div>
      </div>

      <div className="px-6 py-6 flex flex-col items-center">
        <div className="bg-white rounded-xl p-4" data-testid="pass-qr">
          <QRCodeSVG value={passUrl} size={148} level="M" includeMargin={false} />
        </div>
        <p className="text-xs text-white/60 mt-3">Scan at Pickup / Handover Desk</p>

        <span
          className={`mt-4 inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full ${
            ready ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300"
          }`}
          data-testid="pass-status-badge"
        >
          {ready ? <ShieldCheck size={13} /> : <Clock size={13} />}
          {ready ? "Ready to Drive" : "Pending Desk Check-In"}
        </span>
      </div>

      <div className="px-6 pb-6 flex gap-2 print:hidden">
        <button
          type="button"
          onClick={() => window.print()}
          className="flex-1 inline-flex items-center justify-center gap-2 bg-white text-neutral-900 text-sm font-semibold py-2.5 rounded-lg hover:bg-white/90 transition-colors"
          data-testid="pass-print-btn"
        >
          <Printer size={15} /> Print / Save Key Pass
        </button>
        {!embedded && (
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center gap-2 border border-white/20 text-sm px-4 py-2.5 rounded-lg hover:bg-white/10 transition-colors"
            data-testid="pass-close-btn"
          >
            <X size={15} /> Close
          </button>
        )}
      </div>
    </div>
  );
}

/** Modal wrapper used by `/manage-booking`. */
export function DigitalRentalPassModal({ booking, companyName, onClose }) {
  return (
    <div
      className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="digital-pass-modal"
    >
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md my-auto">
        <DigitalRentalPass booking={booking} companyName={companyName} onClose={onClose} />
      </div>
    </div>
  );
}
